import { Button, Card, Col, Container, ListGroup, ListGroupItem, Row } from "react-bootstrap";
import { AppAuthStateProps } from '../../types/propTypes';

import { faChartSimple, faChevronDown, faChevronUp, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { COMPASS_PATIENT_VISITS_WITH_METADATA, FAVORITES_FOR_USERNAME_AND_ACTIVE_PATIENTS, FAVORITE_LISTS_FOR_USERNAME, Favorite, Note, PAST_FAVORITES_FOR_COMPASS_PARTICIPANTS, callQuery } from "../../amplify/appsync/query";
import { CompassParticipant, PatientVisit, PatientVisitMetadata } from '../../amplify/appsync/search-patients';
import { isDemoUser } from "../../utils/demo";
import { getDaysBetweenDates, getHourMinuteDifference, getLatestTime } from "../../utils/time";
import { FavoriteColumnSelectModal } from "../cohort/FavoriteColumnSelectModal";
import { FavoriteContextMenu } from "../cohort/FavoriteContextMenu";
import { FavoriteNewListModal } from "../cohort/FavoriteNewListModal";
import { CompassItem } from "./CompassItem";
import DataExport from "./DataExport";

const FILTER_OPTIONS = ["Active Participants", "In-Progress", "Completed - Revisit", "Completed - 30 Days", "Withdrawn", "All Participants"]

const DEFAULT_COLUMNS = [
    {
        name: "Compass ID",
        id: "study_id",
        size: 2,
    },
    {
        name: "Enrolled Date",
        id: "enrollment_date",
        size: 2,
    },
    {
        name: "Completion Date",
        id: "end_date",
        size: 2,
    },
    {
        name: "Last Monitor",
        id: "last_monitor_data",
        size: 2,
    },
    {
        name: "Last Survey",
        id: "last_survey_data",
        size: 2,
    },
    {
        name: "Monitoring",
        id: "monitoring",
        size: 3,
    },
    {
        name: "Days with Monitor",
        id: "monitor_counts",
        size: 2,
    },
    {
        name: "Days with Survey",
        id: "survey_counts",
        size: 2,
    },
    {
        name: "Age",
        id: "age",
        size: 1,
    },
    {
        name: "Sex",
        id: "gender",
        size: 1,
    },
    {
        name: "Revisit Risk",
        id: "epic_revisit_score",
        size: 2,
    },
    {
        name: "",
        id: "action",
        size: 2,
    },
];

export const Compass = (props: AppAuthStateProps) => {

    const [isLoading, setIsLoading] = useState(false)
    const [sortColId, setSortColId] = useState<string>();
    const [sortColAsc, setSortColAsc] = useState<boolean>(true);
    const [activeFilter, setActiveFilter] = useState("Active Participants")
    const [allPatients, setAllPatients] = useState<(CompassParticipant & PatientVisit & PatientVisitMetadata)[]>([]);
    const [activePatients, setActivePatients] = useState<(CompassParticipant & PatientVisit & PatientVisitMetadata)[]>([]);
    const [mrnToName, setMrnToName] = useState<any>({});
    const [csnToFavorites, setCsnToFavorites] = useState<any>({});
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    const [contextMenu, setContextMenu] = useState<any>({x: 0, y: 0, csn: null});
    const [showNewList, setShowNewList] = useState<boolean>(false);
    const [showColumnSelect, setShowColumnSelect] = useState<boolean>(false);
    const [favoriteLists, setFavoriteLists] = useState<string[]>([]);
    const [mrnToPastFavorites, setMrnToPastFavorites] = useState<any>({});
    const [mrnToPastReviewed, setMrnToPastReviewed] = useState<any>({});

    // @ts-ignore
    const [columns, setColumns] = useState<any[]>(DEFAULT_COLUMNS);

    const getPatientStatus = (patient: any) => {
        if (patient && patient.withdrawn === '1') {
            return "Withdrawn";
        } else if (patient && patient.earliest_revisit_time && getDaysBetweenDates(patient.enrollment_date, patient.earliest_revisit_time) <= 30) {
            return "Completed - Revisit";
        } else if (patient && getDaysBetweenDates(patient.enrollment_date, new Date()) > 30) {
            return "Completed - 30 Days";
        } else {
            return "In-Progress";
        }
    }

    const filterPatientsByFilterType = (filterType: string, patients?: (CompassParticipant & PatientVisit & PatientVisitMetadata)[]) => {
        const pts = patients ? patients : allPatients;

        if (filterType === "Active Participants") {
            return pts.filter((p) => {
                if (p.monitoring !== '0') {
                    return true;
                } else {
                    return false;
                }
            });
        } else if (["In-Progress", "Completed - Revisit", "Completed - 30 Days", "Withdrawn"].includes(filterType)) {
            return pts.filter((patient) => getPatientStatus(patient) === filterType);
        } else {
            return pts;
        }
    }

    const sortPatients = (col: string) => {
        setActivePatients((activePts: any) => {
            const pts = [...activePts];
            let asc = sortColAsc;
            if (sortColId === col) {
                if (sortColAsc === false) {
                    // Reset to original state
                    col = "study_id";
                    setSortColId("study_id");
                    setSortColAsc(true);
                    asc = true;
                } else {
                    asc = false;
                    setSortColAsc(asc);
                }
            } else {
                setSortColId(col);
                setSortColAsc(true);
            }
    
            if (asc) {
                pts.sort((a: any, b: any) => {
                    if (col === "name") {
                        const aName = a["family"] + ", " + a["given"];
                        const bName = b["family"] + ", " + b["given"];
                        return aName.localeCompare(bName, 'en', {numeric: true});
                    } else if (col === "elapsed") {
                        const aTime = `${getHourMinuteDifference(new Date(a["arrival_time"] as string), new Date(getLatestTime(a, props.tenant)))}`;
                        const bTime = `${getHourMinuteDifference(new Date(b["arrival_time"] as string), new Date(getLatestTime(b, props.tenant)))}`;
                        return aTime.localeCompare(bTime, 'en', {numeric: true});
                    } else if (typeof a[col] === "number" || typeof b[col] === "number") {
                        const aVal = a[col] === null ? 0 : a[col];
                        const bVal = b[col] === null ? 0 : b[col];
                        return aVal - bVal;
                    } else if (typeof a[col] === "object" || typeof b[col] === "object") {
                        const aVal = a[col] && a[col][0] ? a[col][0] : 0;
                        const bVal = b[col] && b[col][0] ? b[col][0] : 0;
                        return aVal - bVal;
                    } else {
                        const aVal = a[col] ?? "";
                        const bVal = b[col] ?? "";
                        return aVal.localeCompare(bVal);
                    }
                });
            } else {
                pts.sort((a: any, b: any) => {
                    if (col === "name") {
                        const aName = a["family"] + ", " + a["given"];
                        const bName = b["family"] + ", " + b["given"];
                        return bName.localeCompare(aName, 'en', {numeric: true});
                    } else if (col === "elapsed") {
                        const aTime = `${getHourMinuteDifference(new Date(a["arrival_time"] as string), new Date(getLatestTime(a, props.tenant)))}`;
                        const bTime = `${getHourMinuteDifference(new Date(b["arrival_time"] as string), new Date(getLatestTime(b, props.tenant)))}`;
                        return bTime.localeCompare(aTime, 'en', {numeric: true});
                    } else if (typeof a[col] === "number" || typeof b[col] === "number") {
                        const aVal = a[col] === null ? 0 : a[col];
                        const bVal = b[col] === null ? 0 : b[col];
                        return bVal - aVal;
                    } else if (typeof a[col] === "object" || typeof b[col] === "object") {
                        const aVal = a[col] && a[col][0] ? a[col][0] : 0;
                        const bVal = b[col] && b[col][0] ? b[col][0] : 0;
                        return bVal - aVal;
                    } else {
                        const aVal = a[col] ?? "";
                        const bVal = b[col] ?? "";
                        return bVal.localeCompare(aVal);
                    }
                });
            }
            return pts;
        });
    }

    useEffect(() => {
        setIsLoading(true);
        callQuery({
            operation: COMPASS_PATIENT_VISITS_WITH_METADATA,
        })
        .then((patients: (PatientVisit & CompassParticipant)[]) => {
            setIsLoading(false);

            const augmentedPatients = patients.map((p: any) => {
                return {
                    ...p,
                    given: null,
                    family: null,
                    email: null,
                    phone_mobile: null,
                    phone_home: null,
                    languages: [],
                }
            });
            setAllPatients(augmentedPatients);

            setActivePatients([
                ...filterPatientsByFilterType(activeFilter, augmentedPatients),
            ])
        });

        callQuery({
            operation: FAVORITES_FOR_USERNAME_AND_ACTIVE_PATIENTS,
        })
        .then((scores: any) => {
            const mapFavorites: any = {};
            for (let score of scores) {
                const csn = score["csn"];
                if (score["favorite_type"] !== "recent") {
                    if (!(csn in mapFavorites)) {
                        mapFavorites[csn] = [];
                    }
                    mapFavorites[csn].push(score);
                } 
            }
            setCsnToFavorites(mapFavorites);
        });

        callQuery({
            operation: PAST_FAVORITES_FOR_COMPASS_PARTICIPANTS,
        })
        .then((favorites: any) => {
            const mapFavorites: any = {};
            const mapReviewed: any = {};
            for (let favorite of favorites) {
                console.log("updating favorite", favorite)
                const mrn = favorite["mrn"];
                if (favorite["favorite_type"] === "reviewed") {
                    mapReviewed[mrn] = favorite;
                } else if (favorite["favorite_type"] !== "recent") {
                    if (!(mrn in mapFavorites)) {
                        mapFavorites[mrn] = [];
                    }
                    mapFavorites[mrn].push(favorite);
                }
            }
            setMrnToPastFavorites(mapFavorites);
            setMrnToPastReviewed(mapReviewed);
        });

        callQuery({
            operation: FAVORITE_LISTS_FOR_USERNAME,
        })
        .then((lists: any) => {
            lists = [
                "favorite",
                ...lists.map((list: any) => list.list_name)
            ];
            setFavoriteLists(lists);
        });
    }, []);
    
    useEffect(() => {
        setActivePatients([
            ...filterPatientsByFilterType(activeFilter),
        ])
    }, [activeFilter]);
    
    useEffect(() => {
        setAllPatients((pts: any) => {
            return [
                ...pts.map(((patient: (CompassParticipant & PatientVisit & PatientVisitMetadata)) => {
                    return {
                        ...patient,
                        given: mrnToName[patient.mrn] ? mrnToName[patient.mrn].given : null,
                        family: mrnToName[patient.mrn] ? mrnToName[patient.mrn].family : null,
                        email: mrnToName[patient.mrn] ? mrnToName[patient.mrn].email : null,
                        phone_mobile: mrnToName[patient.mrn] ? mrnToName[patient.mrn].phone_mobile : null,
                        phone_home: mrnToName[patient.mrn] ? mrnToName[patient.mrn].phone_home : null,
                        languages: mrnToName[patient.mrn] ? mrnToName[patient.mrn].languages : null,
                    }
                })),
            ];
        });
        setActivePatients((pts: any) => {
            return [
                ...filterPatientsByFilterType(activeFilter, pts).map(((patient: (CompassParticipant & PatientVisit & PatientVisitMetadata)) => {
                    return {
                        ...patient,
                        given: mrnToName[patient.mrn] ? mrnToName[patient.mrn].given : null,
                        family: mrnToName[patient.mrn] ? mrnToName[patient.mrn].family : null,
                        email: mrnToName[patient.mrn] ? mrnToName[patient.mrn].email : null,
                        phone_mobile: mrnToName[patient.mrn] ? mrnToName[patient.mrn].phone_mobile : null,
                        phone_home: mrnToName[patient.mrn] ? mrnToName[patient.mrn].phone_home : null,
                        languages: mrnToName[patient.mrn] ? mrnToName[patient.mrn].languages : null,
                    }
                })),
            ]
        })
    }, [mrnToName]);


    const handleClear = () => {
        setShowContextMenu(false);
    };

    const updateCsnToFavorites = (csn: string, favoriteType: string, favorite?: Favorite) => {
        console.log("updateCsnToFavorites", favoriteType)
        const map: any = {...csnToFavorites};
        if (!(csn in map)) {
            map[csn] = [];
        }
        if (favorite) {
            // newly saved favorite
            map[csn] = map[csn].filter((v: Favorite) => v.favorite_type !== favorite.favorite_type);
            map[csn].push(favorite);
        } else {
            // favorite deleted
            map[csn] = map[csn].filter((v: Favorite) => {
                console.log("Filtering...", v, v.favorite_type , favoriteType, v.favorite_type !== favoriteType)
                return v.favorite_type !== favoriteType
            });
            
            console.log("updateCsnToFavorites deleted", favoriteType, map)
        }
        setCsnToFavorites(map);
    };

    const updatePatientWithNote = (note: Note) => {
        setActivePatients(activePatients =>
            activePatients.map(pt => 
                pt.csn === note.csn ? { ...pt, note: note.note } : pt
            )
        );
    };

    return (
        <>
            <Container style={{ paddingTop: isDemoUser(props.user) ? '143px' : '64px', paddingLeft: '32px', paddingRight: '32px' }} onClick={handleClear} fluid>

                <Row style={{marginBottom: '12px'}}>
                    <Col md={9}>
                        {
                            FILTER_OPTIONS.map(opt => {
                                if (activeFilter === opt) {
                                    return (
                                        <Button key={opt} size="sm" variant="primary" className="trackboard-quick-actions-btn" onClick={() => {setActiveFilter(opt)}}>{opt} ({filterPatientsByFilterType(opt).length})</Button>
                                    )
                                } else {
                                    return (
                                        <Button key={opt} size="sm" variant="outline-secondary" className="trackboard-quick-actions-btn" onClick={() => {setActiveFilter(opt)}}>{opt} ({filterPatientsByFilterType(opt).length})</Button>
                                    )
                                }
                            })
                        }
                    </Col>
                    <Col md={3} className="text-right trackboard-monitor-updated">
                        <DataExport 
                            data={allPatients}
                            variant="outline-secondary"
                            buttonText="Export All Participants"
                        />
                        <Button size="sm" variant="outline-secondary" className="trackboard-quick-actions-btn" onClick={() => {window.open(`/compass/accrual`, "_blank")}}><FontAwesomeIcon icon={faChartSimple} /> Study Summary</Button>
                    </Col>
                </Row>
                <Row style={{marginBottom: '24px'}}>
                    <Col>
                        <Card className="trackboard-table">
                            <ListGroup variant="flush">
                                <ListGroupItem className="d-flex flex-column trackboard-table-header">
                                    <div style={{display: 'block', width: '100%'}} >
                                        {
                                            columns.map((col) => {
                                                const totalRelativeCols = columns.reduce((acc, obj) => acc + (obj.size ?? 0), 0);
                                                if (col.id === "action") {
                                                    return (
                                                        <div key={col.id} className="text-right" style={{display: 'inline-block', width: `${100 * col.size/totalRelativeCols}%`}}>
                                                            <Button
                                                                variant="primary"
                                                                className={"trackboard-show-btn"}
                                                                onClick={() => setShowColumnSelect(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </Button>
                                                        </div>
                                                    )
                                                } else {
                                                    return (
                                                        <div key={col.id} style={{display: 'inline-block', cursor: 'pointer', width: `${100 * col.size/totalRelativeCols}%`}} onClick={() => sortPatients(col.id)}>{col.name}{sortColId === col.id ? sortColAsc ? <>{' '}<FontAwesomeIcon icon={faChevronDown} /></> : <>{' '}<FontAwesomeIcon icon={faChevronUp} /></> : <></>}</div>
                                                    )
                                                }
                                            })
                                        }
                                    </div>
                                </ListGroupItem>
                                {
                                    isLoading ? (
                                        <>
                                            {
                                                [1, 2, 3].map((v) => (
                                                    <CompassItem 
                                                        key={v}
                                                        /** @ts-ignore */
                                                        patient={null}
                                                        columns={columns} 
                                                        csnToFavorites={csnToFavorites}
                                                        setShowContextMenu={setShowContextMenu}
                                                        setContextMenu={setContextMenu}
                                                        padNotes={true}
                                                        isPlaceholder={true}
                                                        updatePatientWithNote={updatePatientWithNote}
                                                        tenant={props.tenant}
                                                    />
                                                ))
                                            }
                                        </>
                                    ) : (
                                        <>{
                                            activePatients.map((pt) => (
                                                <CompassItem 
                                                    key={pt.study_id}
                                                    patient={pt}
                                                    columns={columns}
                                                    csnToFavorites={csnToFavorites}
                                                    pastFavorites={mrnToPastFavorites[pt.mrn]}
                                                    pastReviewed={mrnToPastReviewed[pt.mrn]}
                                                    padNotes={true}
                                                    setShowContextMenu={setShowContextMenu}
                                                    setContextMenu={setContextMenu}
                                                    updatePatientWithNote={updatePatientWithNote}
                                                    tenant={props.tenant}
                                                />
                                            ))
                                        }</>
                                    )
                                }
                            </ListGroup>
                        </Card>
                    </Col>
                </Row>
                <FavoriteContextMenu show={showContextMenu} contextMenu={contextMenu} lists={favoriteLists} setShow={setShowContextMenu} setShowNewList={setShowNewList} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteNewListModal show={showNewList} setShow={setShowNewList} lists={favoriteLists} setLists={setFavoriteLists} contextMenu={contextMenu} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteColumnSelectModal show={showColumnSelect} setShow={setShowColumnSelect} columns={columns} setColumns={setColumns} allColumns={DEFAULT_COLUMNS} saveKey="trackboard_columns" />
            </Container>
        </>
    );
};
